import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Skeleton,
  useTheme,
  Avatar,
} from "@mui/material";
import {
  calculateDiscountedPrice,
  capitalizeFirstLetter,
  formatCurrency,
  getReadableDateString,
} from "../../utils/commons";
import { green, grey } from "@mui/material/colors";
import {
  GetSubScriptionList,
  ISubscription,
} from "../../services/SubscriptionServices";
import { SubscriptionThumbCard } from "../../components/ContentCard";

interface SubscriptionProps {
  setSelectedId: React.Dispatch<React.SetStateAction<string>>;
  setShowView: React.Dispatch<React.SetStateAction<boolean>>;
}

const SubscriptionTable: React.FC<SubscriptionProps> = ({
  setSelectedId,
  setShowView,
}) => {
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const theme = useTheme();

  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await GetSubScriptionList({ page: currentPage, size: 10 });
      setSubscriptions(data.content);
      setCurrentPage(data.number);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? green[500] : grey[500];
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "70vh",
          padding: 2,
        }}
      >
        <TableContainer sx={{ flex: 1, scrollbarWidth: "none" }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: theme.palette.info.light,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <TableCell
                  align="center"
                  sx={{ minWidth: 150, maxWidth: 150, width: 150 }}
                >
                  Image
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ minWidth: 250, maxWidth: 250, width: 250 }}
                >
                  Subscription Name
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
                >
                  Duration
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
                >
                  Price
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
                >
                  Status
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
                >
                  Created
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ overflowY: "hidden", scrollbarWidth: "none" }}>
              {loading ? (
                <RenderSkeletonRows />
              ) : (
                subscriptions.map((subscription) => (
                  <TableRow
                    key={subscription.id}
                    hover
                    onClick={() => {
                      setSelectedId(subscription.id);
                      setShowView(true);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell
                      align="center"
                      sx={{ minWidth: 150, maxWidth: 150, width: 150 }}
                    >
                      <SubscriptionThumbCard
                        srcUrl={subscription.thumbnailUrl as string}
                        width={"100%"}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ minWidth: 250, maxWidth: 250, width: 250 }}
                    >
                      {capitalizeFirstLetter(subscription.name)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
                    >
                      {subscription.duration > 10000
                        ? "One Time Purchase"
                        : subscription.duration + " " + "days"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
                    >
                      <span>
                        {`${formatCurrency(
                          calculateDiscountedPrice(
                            subscription.price,
                            subscription.discount
                          )
                        )}`}
                        <span
                          style={{
                            fontSize: "0.9em",
                            color: "gray",
                            marginLeft: 8,
                          }}
                        >
                          <s>{`${formatCurrency(subscription.price)}`}</s>
                        </span>
                      </span>
                      <br />
                      <span style={{ fontSize: "0.8em", color: "green" }}>
                        {`${subscription.discount}% off`}
                      </span>
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: getStatusColor(subscription.active),
                        }}
                      >
                        {subscription.active ? "Active" : "Inactive"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
                    >
                      {getReadableDateString(subscription.createdAt)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>{" "}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 1,
          position: "fixed",
          bottom: 10,
          right: 10,
          zIndex: theme.zIndex.appBar,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2">
            Page <b>{currentPage + 1}</b> of {totalPages}
          </Typography>
        </Box>
        <Box>
          <Button
            onClick={() => handleChangePage(currentPage - 1)}
            disabled={currentPage === 0}
          >
            Previous
          </Button>
          <Button
            onClick={() => handleChangePage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
          >
            Next
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default SubscriptionTable;

const RenderSkeletonRows = () => {
  return (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          <TableCell
            sx={{ minWidth: 150, maxWidth: 150, width: 150 }}
            align="center"
          >
            <Skeleton variant="rectangular" width={"100%"} height={100} />
          </TableCell>

          <TableCell
            sx={{ minWidth: 250, maxWidth: 250, width: 250 }}
            align="center"
          >
            <Skeleton variant="rectangular" height={35} />
          </TableCell>
          <TableCell
            sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
            align="center"
          >
            <Skeleton variant="rectangular" height={35} />
          </TableCell>
          <TableCell
            sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
            align="center"
          >
            <Skeleton variant="rectangular" height={35} />
          </TableCell>
          <TableCell
            align="center"
            sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
          >
            <Skeleton variant="rectangular" height={35} />
          </TableCell>

          <TableCell
            sx={{ minWidth: 100, maxWidth: 100, width: 100 }}
            align="center"
          >
            <Skeleton variant="rectangular" height={35} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};
